import * as React from 'react'

import { Box, Button, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useReviewsDataTable } from 'generated/resources/datatable.graphql'
import { PerformanceReviewsReview } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { byAuthorFilter } from './Filters/byAuthorFilter'
import { statusFilter } from './Filters/statusFilter'
import Settings from './Settings'
import { StatusTag } from './helpers/StatusTag'

interface ParticipationProps {
  status: string | null | undefined
  answeredQuestionsCount: number | null | undefined
  questionGroupsCount: number | null | undefined
}

const Participation = ({
  status,
  answeredQuestionsCount,
  questionGroupsCount,
}: ParticipationProps) => {
  if (status === 'draft') return <>-</>

  return (
    <ProgressBar
      showLabel
      amount={answeredQuestionsCount as number}
      total={questionGroupsCount as number}
    />
  )
}

type Props = {
  onAddNewReview?: () => void
}
const ReviewsDataTable: React.FC<Props> = ({ onAddNewReview }) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const source = useReviewsDataTable(
    [
      {
        identifier: 'name',
        title: i18n.t('surveys.index.table.header.name'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ name }}',
        colSpan: 2,
        render: ({ name }) => {
          return <Text.Normal weight="medium">{name}</Text.Normal>
        },
        sortBy: 'name',
      },
      {
        identifier: 'participation',
        title: i18n.t('surveys.index.table.header.participation'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ participation }}',
        colSpan: 2,
        render: ({ status, questionGroupsCount, answeredQuestionsCount }) => {
          return (
            <Participation
              status={status}
              questionGroupsCount={questionGroupsCount}
              answeredQuestionsCount={answeredQuestionsCount}
            />
          )
        },
        sortBy: 'questionGroupsCount',
      },
      {
        identifier: 'status',
        title: i18n.t('surveys.index.table.header.state'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ status }}',
        colSpan: 2,
        render: ({ status }) => {
          return <StatusTag status={status as string} />
        },
        sortBy: 'status',
      },
      {
        identifier: 'startOn',
        title: i18n.t('surveys.index.table.header.state_date'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ startTime }}',
        colSpan: 2,

        render: ({ startOn }) => {
          return startOn ? dateBackendDateTimeToUI(startOn) : '-'
        },
        sortBy: 'startOn',
      },
      {
        identifier: 'endOn',
        title: i18n.t('surveys.index.table.header.end_date'),
        fixed: true,
        kind: ProjectionKind.Custom,
        template: '{{ endOn }}',
        colSpan: 2,
        render: ({ endOn }) => {
          return endOn ? dateBackendDateTimeToUI(endOn) : '-'
        },
        sortBy: 'endOn',
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        width: 100,
        fixed: true,
        render: (review) => {
          return <Settings review={review as PerformanceReviewsReview} />
        },
      },
    ],
    {
      variables: { search: debouncedSearch },
    }
  )

  return (
    <DataTable.Provider
      source={source}
      filters={[statusFilter(source), byAuthorFilter(source)]}
      pagination={{
        kind: 'offset',
        itemsPerPage: 20,
      }}
      sort={{
        path: 'status',
        direction: 'asc',
      }}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          disableDefaultActions
          search={{
            placeholder: i18n.t('common.search_by'),
            value: search,
            onChange: onSearch,
          }}
          actions={
            onAddNewReview ? (
              <Button iconName="add" onClick={onAddNewReview} type="primary">
                {i18n.t('surveys.index.actions.new_review')}
              </Button>
            ) : undefined
          }
        />
        <DataTable.Table />
      </Box>
    </DataTable.Provider>
  )
}

export default ReviewsDataTable
